"use client";

import React, { useEffect } from "react";
import { RefreshCw } from "lucide-react";
import { ButtonOutline } from "@/components/common/StandardButton";
import StandardCard from "../common/StandardCard";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { orderActions } from "@/store/actions/order.actions";

interface ShipmentDetailsProps {
  orderId: string;
}

const ShipmentDetails: React.FC<ShipmentDetailsProps> = ({ orderId }) => {
  const dispatch = useAppDispatch();
  const { selectedOrder, detailsLoading, error } = useAppSelector(
    (state) => state.order,
  );

  // No need to fetch here - parent component handles it

  if (detailsLoading) {
    return (
      <StandardCard
        title="Shipment"
        headerAction={
          <ButtonOutline size="sm" className="flex items-center gap-2" disabled>
            <RefreshCw className="h-4 w-4" />
            Refresh
          </ButtonOutline>
        }
      >
        <div className="space-y-4 animate-pulse">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="h-4 bg-muted rounded w-24"></div>
              <div className="h-4 bg-muted rounded w-16"></div>
            </div>
            <div className="flex justify-between items-center">
              <div className="h-4 bg-muted rounded w-32"></div>
              <div className="h-4 bg-muted rounded w-20"></div>
            </div>
            <div className="flex justify-between items-center">
              <div className="h-4 bg-muted rounded w-28"></div>
              <div className="h-4 bg-muted rounded w-12"></div>
            </div>
          </div>
        </div>
      </StandardCard>
    );
  }

  if (error || !selectedOrder) {
    return (
      <StandardCard
        title="Shipment"
        headerAction={
          <ButtonOutline size="sm" className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </ButtonOutline>
        }
      >
        <div className="text-center text-muted-foreground">
          {error || "No shipment information available"}
        </div>
      </StandardCard>
    );
  }

  const orderSet = selectedOrder.order_sets?.[0];
  const shippingDetails = orderSet?.shipping_details;
  const orderItems = orderSet?.items || [];
  const totalQuantity = selectedOrder.calculation?.total_quantity || 0;
  const totalWeight = orderItems.reduce(
    (sum, item) => sum + (item.shipping_details?.weight || 0) * item.quantity,
    0,
  );
  const chargeableWeight = totalWeight > 0 ? `${totalWeight}g` : "N/A";

  const deliveryCharges = selectedOrder.calculation?.total_charges || 0;

  return (
    <StandardCard
      title="Shipment"
      headerAction={
        <ButtonOutline size="sm" className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </ButtonOutline>
      }
    >
      <div className="space-y-4">
        <div className="text-sm text-muted-foreground">Shipment Details</div>

        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">
              Delivery Charges
            </span>
            <span className="font-medium text-foreground">
              ₹{(deliveryCharges / 100).toFixed(2)}
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Shipment Id</span>
            <span className="font-medium text-foreground">
              {orderSet?.shipping_details.shipment_id}
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Total Items</span>
            <span className="font-medium text-foreground">{totalQuantity}</span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">SKU Count</span>
            <span className="font-medium text-foreground">
              {orderItems.length}
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Status</span>
            <span className="font-medium text-foreground">
              {orderSet?.shipping_status || "Pending"}
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">
              Chargeable Weight
            </span>
            <span className="font-medium text-foreground">
              {chargeableWeight}
            </span>
          </div>

          {shippingDetails?.tracking_id && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Tracking ID</span>
              <span className="font-medium text-foreground">
                {shippingDetails.tracking_id}
              </span>
            </div>
          )}

          {shippingDetails?.courier_name && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Courier</span>
              <span className="font-medium text-foreground">
                {shippingDetails.courier_name}
              </span>
            </div>
          )}
        </div>
      </div>
    </StandardCard>
  );
};

export default ShipmentDetails;
